import React, { useState, useEffect } from 'react';
import { getUsers } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Save, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Production'da frontend ve backend aynı domain'de
const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const Targets = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingTargetId, setEditingTargetId] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Filtre için ay-yıl state'leri
  const currentDate = new Date();
  const [filterMonth, setFilterMonth] = useState(String(currentDate.getMonth() + 1)); // 1-12
  const [filterYear, setFilterYear] = useState(String(currentDate.getFullYear()));

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [usersData, targetsData] = await Promise.all([
        getUsers(),
        axios.get(`${API_BASE}/api/targets?token=${token}`)
      ]);
      setUsers(usersData);
      setTargets(targetsData.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTarget = async () => {
    if (!selectedUser || !targetAmount || !selectedMonth) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editMode && editingTargetId) {
        // Güncelleme
        await axios.put(`${API_BASE}/api/targets/${editingTargetId}?token=${token}`, {
          username: selectedUser,
          target_amount: parseFloat(targetAmount),
          month: parseInt(selectedMonth),
          year: selectedYear
        });
        toast.success('Hedef başarıyla güncellendi');
      } else {
        // Yeni ekleme
        await axios.post(`${API_BASE}/api/targets?token=${token}`, {
          username: selectedUser,
          target_amount: parseFloat(targetAmount),
          month: parseInt(selectedMonth),
          year: selectedYear
        });
        toast.success('Hedef başarıyla kaydedildi');
      }
      
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving target:', error);
      toast.error('Hedef kaydedilirken hata oluştu');
    }
  };

  const handleEditClick = (target) => {
    setEditMode(true);
    setEditingTargetId(target.id);
    setSelectedUser(target.username);
    setTargetAmount(target.target_amount.toString());
    setSelectedMonth(target.month.toString());
    setSelectedYear(target.year);
    setShowDialog(true);
  };

  const handleDeleteTarget = async (targetId) => {
    if (!window.confirm('Bu hedefi silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/targets/${targetId}?token=${token}`);
      toast.success('Hedef başarıyla silindi');
      loadData();
    } catch (error) {
      console.error('Error deleting target:', error);
      toast.error('Hedef silinirken hata oluştu');
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setEditingTargetId(null);
    setSelectedUser('');
    setTargetAmount('');
    setSelectedMonth('');
    setSelectedYear(new Date().getFullYear());
  };

  const handleDialogClose = (open) => {
    setShowDialog(open);
    if (!open) {
      resetForm();
    }
  };

  const getRealizationRate = (username, month, year) => {
    // Kullanıcının o aydaki gerçekleşen karını hesapla
    // Şimdilik 0 döndürüyoruz, backend'den gelecek
    return 0;
  };

  const months = [
    { value: 1, label: 'Ocak' },
    { value: 2, label: 'Şubat' },
    { value: 3, label: 'Mart' },
    { value: 4, label: 'Nisan' },
    { value: 5, label: 'Mayıs' },
    { value: 6, label: 'Haziran' },
    { value: 7, label: 'Temmuz' },
    { value: 8, label: 'Ağustos' },
    { value: 9, label: 'Eylül' },
    { value: 10, label: 'Ekim' },
    { value: 11, label: 'Kasım' },
    { value: 12, label: 'Aralık' }
  ];

  if (loading) {
    return <div className="text-center py-12">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Hedefler</h1>
          <p className="text-gray-600">Kullanıcı hedeflerini yönetin ve gerçekleşme oranlarını takip edin</p>
        </div>
        <Button
          onClick={() => setShowDialog(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          + Yeni Hedef Ekle
        </Button>
      </div>

      {/* Ay-Yıl Filtresi */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Label className="text-sm font-medium text-gray-600 mb-1">Ay</Label>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger>
                <SelectValue placeholder="Ay Seç" />
              </SelectTrigger>
              <SelectContent>
                {months.map(month => (
                  <SelectItem key={month.value} value={String(month.value)}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label className="text-sm font-medium text-gray-600 mb-1">Yıl</Label>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger>
                <SelectValue placeholder="Yıl Seç" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => {
              const today = new Date();
              setFilterMonth(String(today.getMonth() + 1));
              setFilterYear(String(today.getFullYear()));
            }}
            variant="outline"
          >
            Bu Ay
          </Button>
        </div>
      </div>

      {/* Hedefler Tablosu */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="progo-gradient text-gray-800">
              <tr>
                <th className="px-6 py-3 text-left">Kullanıcı</th>
                <th className="px-6 py-3 text-center">Ay</th>
                <th className="px-6 py-3 text-center">Yıl</th>
                <th className="px-6 py-3 text-right">Hedef (₺)</th>
                <th className="px-6 py-3 text-right">Gerçekleşen (₺)</th>
                <th className="px-6 py-3 text-center">Gerçekleşme (%)</th>
                <th className="px-6 py-3 text-center">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Seçilen ay-yıla göre filtrele
                const filteredTargets = targets.filter(target => 
                  target.month === parseInt(filterMonth) && 
                  target.year === parseInt(filterYear)
                );

                return filteredTargets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      {filterMonth && filterYear 
                        ? `${months.find(m => m.value === parseInt(filterMonth))?.label} ${filterYear} için hedef bulunamadı`
                        : 'Henüz hedef tanımlanmamış'}
                    </td>
                  </tr>
                ) : (
                  filteredTargets.map((target, index) => {
                  const realizationRate = target.realization_rate || 0;
                  const realizationColor = realizationRate >= 100 ? 'text-green-600' : realizationRate >= 75 ? 'text-yellow-600' : 'text-red-600';
                  
                  return (
                    <tr key={target.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-6 py-4 font-medium text-gray-800">{target.user_full_name || target.username}</td>
                      <td className="px-6 py-4 text-center">{months.find(m => m.value === target.month)?.label}</td>
                      <td className="px-6 py-4 text-center">{target.year}</td>
                      <td className="px-6 py-4 text-right font-semibold">
                        {target.target_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </td>
                      <td className="px-6 py-4 text-right font-semibold">
                        {(target.realized_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`text-lg font-bold ${realizationColor}`}>
                          %{realizationRate.toFixed(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditClick(target)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteTarget(target.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hedef Ekleme/Düzenleme Dialog */}
      <Dialog open={showDialog} onOpenChange={handleDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editMode ? 'Hedef Düzenle' : 'Yeni Hedef Ekle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Kullanıcı</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger>
                  <SelectValue placeholder="Kullanıcı seçin" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.username}>
                      {u.full_name} ({u.username})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ay</Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ay seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Yıl</Label>
                <Input
                  type="number"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  min="2020"
                  max="2030"
                />
              </div>
            </div>

            <div>
              <Label>Hedef Tutarı (₺)</Label>
              <Input
                type="number"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                placeholder="Örn: 50000"
                step="0.01"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                İptal
              </Button>
              <Button onClick={handleSaveTarget} className="bg-blue-600 hover:bg-blue-700 text-white">
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Targets;
